import { useEffect, useLayoutEffect, useState, type CSSProperties } from 'react';
import {
  isColorDark,
  lightenHex,
  pickReadableMutedColor,
  pickReadableTextColor,
} from '@/lib/player-color-utils';

/** Aligné sur `InstitutionPlayerBranding` du backend */
export type InstitutionPlayerBrandingPayload = {
  institutionId: string;
  logo: string | null;
  logoSmall: string | null;
  logoDark?: string | null;
  logoSmallDark?: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  backgroundColor: string | null;
  sidebarColor: string | null;
  cardColor: string | null;
  textColor: string | null;
  secondaryTextColor: string | null;
  footerText: string | null;
  buttonStyles?: unknown;
  audioPlayerStyles?: unknown;
  notePlayerStyles?: unknown;
};

const DEFAULT_PRIMARY = '#0C1C38';
const DEFAULT_PRIMARY_HOVER = '#19335E';
const DEFAULT_PRIMARY_DEEP = '#061128';

/** Tokens shell institution / player en mode contraste renforcé (malvoyance). */
export const INST_PLAYER_UI_HIGH_CONTRAST_VARS: Record<string, string> = {
  '--inst-player-bg': '#ffffff',
  '--inst-player-surface': '#ffffff',
  '--inst-player-border': '#000000',
  '--inst-player-text': '#000000',
  '--inst-player-text-muted': '#1a1a1a',
  '--inst-player-primary': '#000000',
  '--inst-player-primary-hover': '#000000',
  '--inst-player-primary-deep': '#000000',
  '--inst-player-accent': '#000000',
  '--inst-player-link': '#0000cc',
  '--inst-player-on-surface-accent': '#000000',
  '--inst-player-sidebar-rail-bg': '#ffffff',
  '--inst-player-sidebar-panel-bg': '#ffffff',
  '--inst-player-theme-dark': '0',
};

/** Tokens shell institution / player quand le thème UI est sombre (écrase le branding clair inline). */
export const INST_PLAYER_UI_DARK_VARS: Record<string, string> = {
  '--inst-player-bg': 'hsl(222, 47%, 6%)',
  '--inst-player-surface': 'hsl(217, 33%, 17%)',
  '--inst-player-border': 'hsl(217, 33%, 22%)',
  '--inst-player-text': 'hsl(210, 40%, 96%)',
  '--inst-player-text-muted': 'hsl(215, 25%, 72%)',
  '--inst-player-accent': '#5eead4',
  '--inst-player-link': '#7dd3fc',
  '--inst-player-on-surface-accent': '#5eead4',
  '--inst-player-sidebar-rail-bg': 'hsl(217, 33%, 10%)',
  '--inst-player-sidebar-panel-bg': 'hsl(217, 33%, 12%)',
  '--inst-player-theme-dark': '1',
};

export type UiThemePreference = 'light' | 'dark' | 'system';

export function isUiThemeDark(theme: UiThemePreference, prefersDark = false): boolean {
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  return prefersDark;
}

/** Fusionne le branding institution avec les tokens dark UI (priorité sur cardColor/sidebar blancs). */
export function mergeInstitutionThemeForUiMode(
  brandingStyle: CSSProperties,
  uiDark: boolean,
): CSSProperties {
  if (!uiDark) return brandingStyle;
  const rec = brandingStyle as Record<string, string>;
  const keep: Record<string, string> = {};
  for (const key of [
    '--inst-player-primary',
    '--inst-player-primary-hover',
    '--inst-player-primary-deep',
    '--inst-player-accent',
    '--inst-player-wave-inactive',
    '--inst-player-wave-inactive-dark',
  ]) {
    const v = rec[key];
    if (!v) continue;
    // Une couleur de marque sombre (navy) reste illisible en texte sur le shell sombre.
    if (key === '--inst-player-accent' && isColorDark(v)) continue;
    keep[key] = v;
  }
  return { ...brandingStyle, ...INST_PLAYER_UI_DARK_VARS, ...keep } as CSSProperties;
}

/** Fusionne le branding institution avec les tokens contraste renforcé (priorité sur couleurs charte). */
export function mergeInstitutionThemeForHighContrast(
  brandingStyle: CSSProperties,
): CSSProperties {
  return { ...brandingStyle, ...INST_PLAYER_UI_HIGH_CONTRAST_VARS } as CSSProperties;
}

/**
 * Couleurs teal/vert historiques seedées en base avant la migration charte
 * navy LyrraStudio. Si une institution a encore l'une de ces valeurs
 * stockée (jamais customisée), on la remplace silencieusement par le navy
 * charte pour éviter le flash vert au mount des pages player/dashboard.
 * Les couleurs réellement choisies par une institution (autres que ces
 * valeurs seedées) restent inchangées.
 */
const LEGACY_TEAL_COLORS = new Set(
  [
    '#2a9d8f', '#2A9D8F',
    '#128577',
    '#0b7668',
    '#0a5a50',
    '#21867a',
    '#238b7e',
    '#005a50',
    '#00685d',
    '#00786c',
    '#004d45',
    '#0f5d52',
    '#14b8a6', '#0d9488', '#0f766e',
  ].map((c) => c.toLowerCase()),
);

function normalizeBrandColor(raw: string | null | undefined, fallback: string): string {
  const v = raw?.trim();
  if (!v) return fallback;
  if (LEGACY_TEAL_COLORS.has(v.toLowerCase())) return fallback;
  return v;
}

function readAudioStyle(styles: unknown, key: string): string | undefined {
  if (!styles || typeof styles !== 'object') return undefined;
  const v = (styles as Record<string, unknown>)[key];
  return typeof v === 'string' ? v : undefined;
}

/**
 * Variables CSS pour le player formation / blocs audio & podcast.
 * Les composants utilisent `var(--inst-player-primary, #0C1C38)` etc.
 */
export function institutionBrandingToPlayerCssVars(
  branding: InstitutionPlayerBrandingPayload | null | undefined,
): CSSProperties {
  const vars: Record<string, string> = {
    '--inst-player-primary': DEFAULT_PRIMARY,
    '--inst-player-primary-hover': DEFAULT_PRIMARY_HOVER,
    '--inst-player-primary-deep': DEFAULT_PRIMARY_DEEP,
    '--inst-player-accent': DEFAULT_PRIMARY,
    '--inst-player-bg': '#F8FAFC',
    '--inst-player-surface': '#ffffff',
    '--inst-player-border': '#E8EDF5',
    '--inst-player-text': '#121826',
    '--inst-player-text-muted': '#5B6472',
    '--inst-player-wave-inactive': '#d1d5db',
    '--inst-player-wave-inactive-dark': '#4b5563',
    '--inst-player-sidebar-strip': 'rgba(12, 28, 56, 0.15)',
    '--inst-player-sidebar-border': 'rgba(12, 28, 56, 0.3)',
    '--inst-player-sidebar-rail-bg': 'color-mix(in srgb, #0C1C38 12%, #f9fafb)',
    '--inst-player-sidebar-panel-bg': 'color-mix(in srgb, #0C1C38 4.5%, #f9fafb)',
  };

  if (!branding) {
    applyPlayerContrastTokens(vars, null);
    return vars as unknown as CSSProperties;
  }

  /** Filtre les anciennes valeurs teal seedées en base -> navy charte. */
  const primary = normalizeBrandColor(
    branding.primaryColor
      ?? readAudioStyle(branding.audioPlayerStyles, 'progressColor')
      ?? readAudioStyle(branding.audioPlayerStyles, 'wavePlayedColor')
      ?? null,
    DEFAULT_PRIMARY,
  );
  const accent = normalizeBrandColor(branding.accentColor, primary);

  vars['--inst-player-primary'] = primary;
  vars['--inst-player-accent'] = accent;
  vars['--inst-player-primary-hover'] = accent;
  vars['--inst-player-primary-deep'] = accent;

  const waveUnplayed =
    readAudioStyle(branding.audioPlayerStyles, 'waveUnplayedColor')
    || readAudioStyle(branding.audioPlayerStyles, 'barInactiveColor');
  if (waveUnplayed) {
    vars['--inst-player-wave-inactive'] = waveUnplayed;
  }

  if (branding.backgroundColor?.trim()) vars['--inst-player-bg'] = branding.backgroundColor.trim();
  if (branding.cardColor?.trim()) vars['--inst-player-surface'] = branding.cardColor.trim();
  if (branding.sidebarColor?.trim()) {
    const s = branding.sidebarColor.trim();
    vars['--inst-player-sidebar-strip'] = `${s}26`;
    vars['--inst-player-sidebar-border'] = `${s}4d`;
  }
  if (branding.textColor?.trim()) vars['--inst-player-text'] = branding.textColor.trim();
  if (branding.secondaryTextColor?.trim()) vars['--inst-player-text-muted'] = branding.secondaryTextColor.trim();

  const pageBg = vars['--inst-player-bg'];
  const sidebarTint = branding.sidebarColor?.trim() || primary;
  vars['--inst-player-sidebar-rail-bg'] = `color-mix(in srgb, ${sidebarTint} 14%, ${pageBg})`;
  vars['--inst-player-sidebar-panel-bg'] = `color-mix(in srgb, ${sidebarTint} 5.5%, ${pageBg})`;

  applyPlayerContrastTokens(vars, branding);

  return vars as unknown as CSSProperties;
}

/**
 * Ajuste texte / liens / bordures quand le fond ou les cartes sont sombres (ex. navy #0C1C38).
 * Évite le texte #111827 illisible sur fond navy après migration charte.
 */
function applyPlayerContrastTokens(
  vars: Record<string, string>,
  branding: InstitutionPlayerBrandingPayload | null | undefined,
): void {
  const surface = vars['--inst-player-surface'] ?? '#ffffff';
  const bg = vars['--inst-player-bg'] ?? '#f9fafb';
  const primary = vars['--inst-player-primary'] ?? DEFAULT_PRIMARY;

  const surfaceDark =
    isColorDark(branding?.cardColor?.trim() || surface) || isColorDark(surface);
  const bgDark =
    isColorDark(branding?.backgroundColor?.trim() || bg) || isColorDark(bg);
  const themeDark = surfaceDark || bgDark;

  vars['--inst-player-theme-dark'] = themeDark ? '1' : '0';

  const contrastBg = surfaceDark ? surface : bgDark ? bg : surface;

  vars['--inst-player-text'] = pickReadableTextColor(
    contrastBg,
    branding?.textColor?.trim() || vars['--inst-player-text'],
  );
  vars['--inst-player-text-muted'] = pickReadableMutedColor(
    contrastBg,
    branding?.secondaryTextColor?.trim() || vars['--inst-player-text-muted'],
  );

  if (themeDark) {
    vars['--inst-player-link'] = isColorDark(primary)
      ? lightenHex(primary, 0.62)
      : primary;
    vars['--inst-player-on-surface-accent'] = vars['--inst-player-link'];
    vars['--inst-player-primary-deep'] = vars['--inst-player-link'];
    vars['--inst-player-border'] = `color-mix(in srgb, ${vars['--inst-player-text-muted']} 38%, transparent)`;
  } else {
    vars['--inst-player-link'] = primary;
    vars['--inst-player-on-surface-accent'] =
      vars['--inst-player-primary-deep'] ?? DEFAULT_PRIMARY_HOVER;
  }
}

export function isInstPlayerThemeDark(
  cssVars: CSSProperties | Record<string, string> | null | undefined,
): boolean {
  if (!cssVars || typeof cssVars !== 'object') return false;
  return (cssVars as Record<string, string>)['--inst-player-theme-dark'] === '1';
}

/**
 * Copie les variables `--inst-player-*` définies en inline sur le shell lecteur (ex. portail plein écran hors arbre DOM).
 */
export function snapshotPlayerInstitutionCssVars(): CSSProperties {
  if (typeof document === 'undefined') return {};
  const el = document.querySelector('.player-institution-theme') as HTMLElement | null;
  if (!el?.style?.length) return {};
  const out: Record<string, string> = {};
  for (let i = 0; i < el.style.length; i += 1) {
    const prop = el.style.item(i);
    if (prop.startsWith('--inst-player')) {
      out[prop] = el.style.getPropertyValue(prop).trim();
    }
  }
  return out as unknown as CSSProperties;
}

/**
 * Normalise la réponse GET EduFlow (player / détail) : `{ success, data }` ou `data` imbriqué.
 */
export function unwrapEduflowPlayerApiPayload(resp: unknown): Record<string, unknown> | null {
  if (resp == null || typeof resp !== 'object' || Array.isArray(resp)) return null;
  let cur: unknown = resp;
  for (let i = 0; i < 5; i += 1) {
    if (!cur || typeof cur !== 'object' || Array.isArray(cur)) return null;
    const o = cur as Record<string, unknown>;
    if (Array.isArray(o.blocks)) return o;
    if (o.flow != null && o.edges !== undefined) return o;
    const inner = o.data;
    if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
      cur = inner;
      continue;
    }
    return null;
  }
  return null;
}

/** Chemin brut du logo institution selon thème clair/sombre et menu réduit ou non. */
export function pickInstitutionLogoPath(
  branding: InstitutionPlayerBrandingPayload | null | undefined,
  options: { isDark: boolean; collapsed?: boolean },
): string | null {
  if (!branding) return null;
  const collapsed = options.collapsed ?? false;
  const light = branding.logo?.trim() || null;
  const dark = branding.logoDark?.trim() || null;
  const lightSmall = branding.logoSmall?.trim() || null;
  const darkSmall = branding.logoSmallDark?.trim() || null;
  if (collapsed) {
    return options.isDark ? darkSmall || lightSmall || dark || light : lightSmall || light;
  }
  return options.isDark ? dark || light : light;
}

export function extractInstitutionBrandingFromFlowPayload(
  payload: Record<string, unknown> | null | undefined,
): InstitutionPlayerBrandingPayload | null {
  const raw = payload?.institutionBranding;
  if (!raw || typeof raw !== 'object') return null;
  const b = raw as InstitutionPlayerBrandingPayload;
  if (!b.institutionId) return null;
  return b;
}

/** Couleur primaire effective du thème institution (player), pour libs qui exigent un hex */
export function useInstitutionPlayerPrimary(fallback = DEFAULT_PRIMARY): string {
  const [c, setC] = useState(fallback);
  const [styleRevision, setStyleRevision] = useState(0);

  useLayoutEffect(() => {
    const el = document.querySelector('.player-institution-theme');
    if (!el) return;
    const v = getComputedStyle(el).getPropertyValue('--inst-player-primary').trim();
    if (v) setC(v);
  }, [styleRevision]);

  useEffect(() => {
    const el = document.querySelector('.player-institution-theme');
    if (!el) return;
    const obs = new MutationObserver(() => setStyleRevision((n) => n + 1));
    obs.observe(el, { attributes: true, attributeFilter: ['style', 'class'] });
    return () => obs.disconnect();
  }, []);

  return c;
}
